// Offers drawer: opens a slide-in panel listing products from the all-products
// collection filtered by the promo tag carried on the clicked badge link.
class OffersDrawer extends HTMLElement {
  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.currentTag = null;
  }

  get panel() {
    return this.querySelector(".offers-drawer__panel");
  }

  get productsEl() {
    return this.querySelector(".js-offers-products");
  }

  get emptyEl() {
    return this.querySelector(".offers-drawer__empty");
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || "offers-drawer-products";
    document.addEventListener("keydown", this.onKeydown);
    this.querySelectorAll("[data-offers-close]").forEach((el) => {
      el.addEventListener("click", this.onCloseClick);
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown);
    this.querySelectorAll("[data-offers-close]").forEach((el) => {
      el.removeEventListener("click", this.onCloseClick);
    });
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.classList.contains("active")) {
      event.stopPropagation();
      this.close();
    }
  }

  open(tag, rawTag) {
    this.classList.add("active");
    if (this.panel) this.panel.classList.add("open");
    // Only refetch when the requested offer changed
    if (tag !== this.currentTag) {
      this.currentTag = tag;
      this.fetchProducts(tag, rawTag);
    }
  }

  close() {
    this.classList.remove("active", "loading");
    if (this.panel) this.panel.classList.remove("open");
    this.currentTag = null;
  }

  fetchProducts(tag, rawTag) {
    this.classList.add("loading");
    if (this.emptyEl) this.emptyEl.classList.add("hidden");
    if (this.productsEl) this.productsEl.innerHTML = "";
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    const params = new URLSearchParams({
      section_id: this.sectionId,
      view: "offers",
    });
    if (rawTag) params.set("offer_tag", rawTag);
    const url = `${root}collections/all/${encodeURIComponent(
      tag,
    )}?${params.toString()}`;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const products = html.querySelector(".js-offers-products");
        const countEl = products?.querySelector("[data-product-count]");
        const productCount = countEl
          ? parseInt(countEl.dataset.productCount || "0", 10)
          : 0;
        if (products && (products.children.length > 0 || productCount > 0)) {
          if (this.productsEl) this.productsEl.innerHTML = products.innerHTML;
          if (this.emptyEl) this.emptyEl.classList.add("hidden");
        } else {
          if (this.emptyEl) this.emptyEl.classList.remove("hidden");
        }
      })
      .catch((e) => {
        console.error(e);
        if (this.emptyEl) this.emptyEl.classList.remove("hidden");
      })
      .finally(() => {
        this.classList.remove("loading");
      });
  }
}

function getDrawerForTrigger(trigger) {
  if (trigger.closest(".page-cart, .cart-page, #MainCartItems")) {
    return document.querySelector(".offers-drawer--page");
  }

  const cartDrawer = trigger.closest("cart-drawer");
  if (cartDrawer) {
    return cartDrawer.querySelector("offers-drawer-element");
  }

  return document.querySelector("offers-drawer-element");
}

function handleOfferTagClick(event) {
  const trigger = event.target.closest("[data-offer-tag]");
  if (!trigger) return;

  const drawer = getDrawerForTrigger(trigger);
  if (!drawer || drawer.contains(trigger)) return;

  const tag = trigger.getAttribute("data-offer-tag");
  if (!tag) return;

  event.preventDefault();

  const rawTag = trigger.getAttribute("data-offer-tag-raw") || "";
  drawer.open(tag, rawTag);
}

function closeOffersDrawer(drawer) {
  if (drawer && typeof drawer.close === "function") {
    drawer.close();
  }
}

function getActiveOffersDrawer(cartDrawer) {
  return cartDrawer?.querySelector("offers-drawer-element.offers-drawer.active");
}

// When promo is open, backdrop click should close promo first — not leave it active for the next cart open
function handleCartBackdropClick(event) {
  const modalOverlay = event.target.closest("cart-drawer modal-overlay");
  if (!modalOverlay) return;

  const cartDrawer = modalOverlay.closest("cart-drawer");
  const offersDrawer = getActiveOffersDrawer(cartDrawer);
  if (!offersDrawer) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  closeOffersDrawer(offersDrawer);
}

function handleCartModalClosed(event) {
  const modal = event.detail?.modal;
  if (!modal || modal.tagName !== "CART-DRAWER") return;

  modal.querySelectorAll("offers-drawer-element.active").forEach((drawer) => {
    closeOffersDrawer(drawer);
  });
}

if (!customElements.get("offers-drawer-element")) {
  customElements.define("offers-drawer-element", OffersDrawer);
}

// Capture phase ensures navigation is blocked before the link default action runs
if (!window.__offersDrawerClickBound) {
  window.__offersDrawerClickBound = true;
  document.addEventListener("click", handleOfferTagClick, true);
  document.addEventListener("click", handleCartBackdropClick, true);
  document.addEventListener("modal:closed", handleCartModalClosed);
}
